import {
  ApiError,
  type IntegrationSettingsView,
  queryKeys,
  type UpdateIntegrationSettingsInput,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

type SecretPreview = { configured: boolean; last4: string | null; source: 'db' | 'env' | null }

/**
 * A credential the server never hands back. Blank means "leave it alone" —
 * typing a new value replaces it, and Clear reverts to whatever the server
 * environment provides (or to nothing, for email, which has no environment
 * fallback at all).
 */
function SecretField({
  label,
  hint,
  preview,
  value,
  onChange,
  cleared,
  onClear,
  error,
}: {
  label: string
  hint?: string
  preview: SecretPreview
  value: string
  onChange: (value: string) => void
  cleared: boolean
  onClear: () => void
  error?: string
}) {
  const status = cleared ? (
    <Badge tone="warning">Will revert to server default on save</Badge>
  ) : preview.configured ? (
    <Badge tone="success">Configured · ends {preview.last4}</Badge>
  ) : preview.source === 'env' ? (
    <Badge tone="info">Using server environment</Badge>
  ) : (
    <Badge tone="neutral">Not configured</Badge>
  )

  return (
    <Field
      label={label}
      hint={hint ?? 'Enter a new value to replace it — leave blank to keep the current one.'}
      error={error}
    >
      {(props) => (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              placeholder={preview.configured ? '••••••••••••' : 'Not set'}
              value={value}
              disabled={cleared}
              onChange={(e) => onChange(e.target.value)}
              invalid={!!error}
            />
            {preview.source === 'db' && !cleared ? (
              <Button type="button" variant="ghost" size="sm" onClick={onClear}>
                Clear
              </Button>
            ) : cleared ? (
              <Button type="button" variant="ghost" size="sm" onClick={onClear}>
                Undo
              </Button>
            ) : null}
          </div>
          {status}
        </div>
      )}
    </Field>
  )
}

function useIntegrationSettings() {
  return useQuery({
    queryKey: queryKeys.integrationSettings(),
    queryFn: () => api.get<IntegrationSettingsView>('/settings/integrations'),
  })
}

function useSaveIntegrationSettings() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null)

  const save = useMutation({
    mutationFn: (patch: UpdateIntegrationSettingsInput) =>
      api.patch('/settings/integrations', patch),
    onSuccess: async () => {
      setFieldErrors(null)
      await queryClient.invalidateQueries({ queryKey: queryKeys.integrationSettings() })
      toast.success('Saved', 'Takes effect within a minute, without restarting anything.')
    },
    onError: (error) => {
      setFieldErrors(error instanceof ApiError ? error.fieldErrors : null)
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined)
    },
  })

  return { save, fieldErrors }
}

/**
 * Integration credentials the admin dashboard can set instead of relying on
 * whoever manages the server. Three cards — Payments, SMS, Email — each save
 * independently, the same way the Shop tab's settings card does.
 */
export function IntegrationsTab() {
  const { data, isLoading } = useIntegrationSettings()

  return (
    <div className="space-y-5">
      <PaymentsCard data={data} isLoading={isLoading} />
      <ShippingCard data={data} isLoading={isLoading} />
      <SmsCard data={data} isLoading={isLoading} />
      <VoiceCard data={data} isLoading={isLoading} />
      <MailgunCard data={data} isLoading={isLoading} />
      <EmailCard data={data} isLoading={isLoading} />
      <AiCard data={data} isLoading={isLoading} />
    </div>
  )
}

/**
 * EasyPost (t0800) — live carrier rates, label purchase, and tracking for
 * shipped orders. No environment fallback, same reasoning as Mailgun: set
 * from the dashboard, or the feature is off. `shippingLabelsEnabled` needs
 * both an API key *and* a complete ship-from address, so the status alert
 * below spells out whichever half is still missing.
 */
function ShippingCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [apiKey, setApiKey] = useState('')
  const [clearApiKey, setClearApiKey] = useState(false)
  const [name, setName] = useState('')
  const [street1, setStreet1] = useState('')
  const [street2, setStreet2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [phone, setPhone] = useState('')

  useEffect(() => {
    if (!data) return
    setApiKey('')
    setClearApiKey(false)
    setName(data.shipFromName.value ?? '')
    setStreet1(data.shipFromStreet1.value ?? '')
    setStreet2(data.shipFromStreet2.value ?? '')
    setCity(data.shipFromCity.value ?? '')
    setState(data.shipFromState.value ?? '')
    setZip(data.shipFromZip.value ?? '')
    setPhone(data.shipFromPhone.value ?? '')
  }, [data])

  const nameChanged = data ? name !== (data.shipFromName.value ?? '') : false
  const street1Changed = data ? street1 !== (data.shipFromStreet1.value ?? '') : false
  const street2Changed = data ? street2 !== (data.shipFromStreet2.value ?? '') : false
  const cityChanged = data ? city !== (data.shipFromCity.value ?? '') : false
  const stateChanged = data ? state !== (data.shipFromState.value ?? '') : false
  const zipChanged = data ? zip !== (data.shipFromZip.value ?? '') : false
  const phoneChanged = data ? phone !== (data.shipFromPhone.value ?? '') : false
  const dirty =
    !!apiKey ||
    clearApiKey ||
    nameChanged ||
    street1Changed ||
    street2Changed ||
    cityChanged ||
    stateChanged ||
    zipChanged ||
    phoneChanged

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (clearApiKey) patch.easypostApiKey = null
    else if (apiKey) patch.easypostApiKey = apiKey
    if (nameChanged) patch.shipFromName = name || null
    if (street1Changed) patch.shipFromStreet1 = street1 || null
    if (street2Changed) patch.shipFromStreet2 = street2 || null
    if (cityChanged) patch.shipFromCity = city || null
    if (stateChanged) patch.shipFromState = state || null
    if (zipChanged) patch.shipFromZip = zip || null
    if (phoneChanged) patch.shipFromPhone = phone || null
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Shipping (EasyPost)</CardTitle>
        <CardDescription>
          Live carrier rates, label printing, and tracking for shipped orders — bought from an
          order's own page once this is set up. Checkout itself still charges the flat shipping fee
          set in Settings → Shop.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data ? (
          <Alert tone={data.shippingLabelsEnabled ? 'success' : 'info'}>
            {data.shippingLabelsEnabled
              ? 'Shipping labels are on.'
              : data.easypostApiKey.configured
                ? 'Off — add a complete ship-from address below.'
                : 'Off — add an EasyPost API key below.'}
          </Alert>
        ) : null}

        <SecretField
          label="API key"
          hint="EasyPost dashboard → API Keys. A test key works for rate quotes; a production key is needed to buy real labels."
          preview={data?.easypostApiKey ?? { configured: false, last4: null, source: null }}
          value={apiKey}
          onChange={setApiKey}
          cleared={clearApiKey}
          onClear={() => {
            setClearApiKey((v) => !v)
            setApiKey('')
          }}
          error={fieldErrors?.easypostApiKey?.[0]}
        />

        <div>
          <p className="mb-2 text-sm font-medium">Ship from</p>
          <div className="space-y-3">
            <Field label="Name / business">
              {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
            </Field>
            <Field label="Street address">
              {(props) => (
                <Input {...props} value={street1} onChange={(e) => setStreet1(e.target.value)} />
              )}
            </Field>
            <Field label="Suite / unit (optional)">
              {(props) => (
                <Input {...props} value={street2} onChange={(e) => setStreet2(e.target.value)} />
              )}
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="City">
                {(props) => <Input {...props} value={city} onChange={(e) => setCity(e.target.value)} />}
              </Field>
              <Field label="State">
                {(props) => (
                  <Input
                    {...props}
                    value={state}
                    maxLength={2}
                    onChange={(e) => setState(e.target.value.toUpperCase())}
                    invalid={!!fieldErrors?.shipFromState}
                  />
                )}
              </Field>
              <Field label="ZIP">
                {(props) => (
                  <Input
                    {...props}
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    invalid={!!fieldErrors?.shipFromZip}
                  />
                )}
              </Field>
            </div>
            <Field label="Phone" hint="Carriers require a phone number for the ship-from address.">
              {(props) => <Input {...props} value={phone} onChange={(e) => setPhone(e.target.value)} />}
            </Field>
          </div>
        </div>

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending || isLoading}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>

        {data?.easypostWebhookUrl ? (
          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-sm font-medium">Tracking webhook</p>
            <p className="text-xs text-foreground-muted">
              In the EasyPost dashboard, add a webhook pointing here, subscribed to Tracker events —
              this is what keeps tracking status and delivery up to date automatically.
            </p>
            <WebhookUrlField label="Webhook URL" url={data.easypostWebhookUrl} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function PaymentsCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [publishableKey, setPublishableKey] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [clearSecretKey, setClearSecretKey] = useState(false)
  const [clearWebhookSecret, setClearWebhookSecret] = useState(false)

  useEffect(() => {
    if (!data) return
    setPublishableKey(data.stripePublishableKey.value ?? '')
    setSecretKey('')
    setWebhookSecret('')
    setClearSecretKey(false)
    setClearWebhookSecret(false)
  }, [data])

  const publishableChanged = data
    ? publishableKey !== (data.stripePublishableKey.value ?? '')
    : false
  const dirty =
    publishableChanged || !!secretKey || !!webhookSecret || clearSecretKey || clearWebhookSecret

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (publishableChanged) patch.stripePublishableKey = publishableKey || null
    if (clearSecretKey) patch.stripeSecretKey = null
    else if (secretKey) patch.stripeSecretKey = secretKey
    if (clearWebhookSecret) patch.stripeWebhookSecret = null
    else if (webhookSecret) patch.stripeWebhookSecret = webhookSecret
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Payments (Stripe)</CardTitle>
        <CardDescription>
          Lets customers pay by card at checkout. Without a secret key, checkout only offers paying
          in store.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data ? (
          <Alert tone={data.cardPaymentEnabled ? 'success' : 'info'}>
            {data.cardPaymentEnabled
              ? 'Card checkout is on.'
              : 'Card checkout is off — a secret key and publishable key are both required.'}
          </Alert>
        ) : null}

        <Field label="Publishable key" hint="Ships to the browser — not a secret.">
          {(props) => (
            <Input
              {...props}
              value={publishableKey}
              disabled={isLoading}
              onChange={(e) => setPublishableKey(e.target.value)}
              placeholder="pk_live_…"
            />
          )}
        </Field>

        <SecretField
          label="Secret key"
          preview={data?.stripeSecretKey ?? { configured: false, last4: null, source: null }}
          value={secretKey}
          onChange={setSecretKey}
          cleared={clearSecretKey}
          onClear={() => {
            setClearSecretKey((v) => !v)
            setSecretKey('')
          }}
          error={fieldErrors?.stripeSecretKey?.[0]}
        />

        <SecretField
          label="Webhook signing secret"
          hint="From the Stripe dashboard's webhook endpoint. Required in production before a card payment can be marked paid."
          preview={data?.stripeWebhookSecret ?? { configured: false, last4: null, source: null }}
          value={webhookSecret}
          onChange={setWebhookSecret}
          cleared={clearWebhookSecret}
          onClear={() => {
            setClearWebhookSecret((v) => !v)
            setWebhookSecret('')
          }}
          error={fieldErrors?.stripeWebhookSecret?.[0]}
        />

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

function SmsCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [provider, setProvider] = useState<string>('server-default')
  const [accountSid, setAccountSid] = useState('')
  const [authToken, setAuthToken] = useState('')
  const [fromNumber, setFromNumber] = useState('')
  const [clearAuthToken, setClearAuthToken] = useState(false)

  useEffect(() => {
    if (!data) return
    setProvider(
      data.smsProvider.source === 'db' ? (data.smsProvider.value ?? 'console') : 'server-default',
    )
    setAccountSid(data.twilioAccountSid.value ?? '')
    setAuthToken('')
    setFromNumber(data.twilioFromNumber.value ?? '')
    setClearAuthToken(false)
  }, [data])

  const providerChanged = data
    ? provider !==
      (data.smsProvider.source === 'db' ? (data.smsProvider.value ?? 'console') : 'server-default')
    : false
  const sidChanged = data ? accountSid !== (data.twilioAccountSid.value ?? '') : false
  const fromChanged = data ? fromNumber !== (data.twilioFromNumber.value ?? '') : false
  const dirty = providerChanged || sidChanged || fromChanged || !!authToken || clearAuthToken

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (providerChanged) {
      patch.smsProvider =
        provider === 'server-default' ? null : (provider as 'console' | 'twilio' | 'disabled')
    }
    if (sidChanged) patch.twilioAccountSid = accountSid || null
    if (fromChanged) patch.twilioFromNumber = fromNumber || null
    if (clearAuthToken) patch.twilioAuthToken = null
    else if (authToken) patch.twilioAuthToken = authToken
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>SMS (Twilio)</CardTitle>
        <CardDescription>
          Sends the sign-in code a customer receives by text. "Server default" leaves it exactly as
          the deployment configures it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Provider">
          {(props) => (
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger {...props} disabled={isLoading}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="server-default">Server default (environment)</SelectItem>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="console">Console (development only)</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field label="Account SID">
          {(props) => (
            <Input
              {...props}
              value={accountSid}
              disabled={isLoading}
              onChange={(e) => setAccountSid(e.target.value)}
              placeholder="AC…"
              invalid={!!fieldErrors?.twilioAccountSid}
            />
          )}
        </Field>

        <SecretField
          label="Auth token"
          preview={data?.twilioAuthToken ?? { configured: false, last4: null, source: null }}
          value={authToken}
          onChange={setAuthToken}
          cleared={clearAuthToken}
          onClear={() => {
            setClearAuthToken((v) => !v)
            setAuthToken('')
          }}
          error={fieldErrors?.twilioAuthToken?.[0]}
        />

        <Field label="From number" hint="E.164, e.g. +18885550123">
          {(props) => (
            <Input
              {...props}
              value={fromNumber}
              disabled={isLoading}
              onChange={(e) => setFromNumber(e.target.value)}
              invalid={!!fieldErrors?.twilioFromNumber}
            />
          )}
        </Field>

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>

        {data ? (
          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-sm font-medium">Inbound support (t0799)</p>
            <p className="text-xs text-foreground-muted">
              Paste these into the Twilio console under your number's settings — Messaging for the
              first, Voice for the second. Who calls ring to, business hours and the voicemail
              greeting are set on Settings → Support.
            </p>
            <div className="space-y-1.5">
              <WebhookUrlField label="SMS webhook URL" url={data.smsInboundWebhookUrl} />
              <WebhookUrlField label="Voice webhook URL" url={data.voiceInboundWebhookUrl} />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function WebhookUrlField({ label, url }: { label: string; url: string }) {
  const toast = useToast()
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide text-foreground-subtle">{label}</p>
      <div className="flex items-center gap-2">
        <Input readOnly value={url} className="font-identifier" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(url)
            toast.success('Copied')
          }}
        >
          Copy
        </Button>
      </div>
    </div>
  )
}

/**
 * Browser calling (t0869) — an API Key and TwiML App on top of the SMS
 * card's Twilio account SID, auth token and from number just above. All six
 * together are what let staff place and receive calls from the admin
 * dashboard instead of a physical handset; who actually receives inbound
 * browser calls is set on Settings → Support, same as phone ring targets.
 */
function VoiceCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [apiKeySid, setApiKeySid] = useState('')
  const [apiKeySecret, setApiKeySecret] = useState('')
  const [twimlAppSid, setTwimlAppSid] = useState('')
  const [clearApiKeySecret, setClearApiKeySecret] = useState(false)

  useEffect(() => {
    if (!data) return
    setApiKeySid(data.twilioApiKeySid.value ?? '')
    setApiKeySecret('')
    setTwimlAppSid(data.twilioTwimlAppSid.value ?? '')
    setClearApiKeySecret(false)
  }, [data])

  const sidChanged = data ? apiKeySid !== (data.twilioApiKeySid.value ?? '') : false
  const appChanged = data ? twimlAppSid !== (data.twilioTwimlAppSid.value ?? '') : false
  const dirty = sidChanged || appChanged || !!apiKeySecret || clearApiKeySecret

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (sidChanged) patch.twilioApiKeySid = apiKeySid || null
    if (appChanged) patch.twilioTwimlAppSid = twimlAppSid || null
    if (clearApiKeySecret) patch.twilioApiKeySecret = null
    else if (apiKeySecret) patch.twilioApiKeySecret = apiKeySecret
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Browser calling</CardTitle>
        <CardDescription>
          Lets staff place and receive calls from inside the admin dashboard. Rides the Twilio
          account SID, auth token and from number set on the SMS card above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data ? (
          <Alert tone={data.voiceCallingEnabled ? 'success' : 'info'}>
            {data.voiceCallingEnabled
              ? 'Browser calling is on.'
              : 'Off — needs an API key, a TwiML App, and the Twilio account SID/auth token/from number above.'}
          </Alert>
        ) : null}

        <Field
          label="API key SID"
          hint="Twilio console → Account → API keys & tokens. Standard key."
        >
          {(props) => (
            <Input
              {...props}
              value={apiKeySid}
              disabled={isLoading}
              onChange={(e) => setApiKeySid(e.target.value)}
              placeholder="SK…"
              invalid={!!fieldErrors?.twilioApiKeySid}
            />
          )}
        </Field>

        <SecretField
          label="API key secret"
          preview={data?.twilioApiKeySecret ?? { configured: false, last4: null, source: null }}
          value={apiKeySecret}
          onChange={setApiKeySecret}
          cleared={clearApiKeySecret}
          onClear={() => {
            setClearApiKeySecret((v) => !v)
            setApiKeySecret('')
          }}
          error={fieldErrors?.twilioApiKeySecret?.[0]}
        />

        <Field
          label="TwiML App SID"
          hint="Twilio console → Voice → TwiML → TwiML Apps. Set its Voice URL to the address below."
        >
          {(props) => (
            <Input
              {...props}
              value={twimlAppSid}
              disabled={isLoading}
              onChange={(e) => setTwimlAppSid(e.target.value)}
              placeholder="AP…"
              invalid={!!fieldErrors?.twilioTwimlAppSid}
            />
          )}
        </Field>

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>

        {data ? (
          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-sm font-medium">Outbound call webhook</p>
            <p className="text-xs text-foreground-muted">
              Paste this into the TwiML App above as its Voice request URL, method POST. Who
              receives inbound calls in the browser is set on Settings → Support.
            </p>
            <WebhookUrlField
              label="Voice outbound webhook URL"
              url={data.voiceOutboundWebhookUrl}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function MailgunCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [domain, setDomain] = useState('')
  const [region, setRegion] = useState<'us' | 'eu'>('us')
  const [apiKey, setApiKey] = useState('')
  const [webhookSigningKey, setWebhookSigningKey] = useState('')
  const [clearApiKey, setClearApiKey] = useState(false)
  const [clearWebhookSigningKey, setClearWebhookSigningKey] = useState(false)

  useEffect(() => {
    if (!data) return
    setDomain(data.mailgunDomain.value ?? '')
    setRegion(data.mailgunRegion.value ?? 'us')
    setApiKey('')
    setWebhookSigningKey('')
    setClearApiKey(false)
    setClearWebhookSigningKey(false)
  }, [data])

  const domainChanged = data ? domain !== (data.mailgunDomain.value ?? '') : false
  const regionChanged = data ? region !== (data.mailgunRegion.value ?? 'us') : false
  const dirty =
    domainChanged ||
    regionChanged ||
    !!apiKey ||
    !!webhookSigningKey ||
    clearApiKey ||
    clearWebhookSigningKey

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (domainChanged) patch.mailgunDomain = domain || null
    if (regionChanged) patch.mailgunRegion = region
    if (clearApiKey) patch.mailgunApiKey = null
    else if (apiKey) patch.mailgunApiKey = apiKey
    if (clearWebhookSigningKey) patch.mailgunWebhookSigningKey = null
    else if (webhookSigningKey) patch.mailgunWebhookSigningKey = webhookSigningKey
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Mail (Mailgun)</CardTitle>
        <CardDescription>
          Powers the Mail inbox in the sidebar — sending and receiving for every address set up
          there — and, once saved, becomes the transport for sign-in codes and staff invites too.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Domain" hint="The domain added and verified in the Mailgun dashboard.">
          {(props) => (
            <Input
              {...props}
              value={domain}
              disabled={isLoading}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="mevasertech.net"
              invalid={!!fieldErrors?.mailgunDomain}
            />
          )}
        </Field>

        <Field label="Region" hint="Must match the region the Mailgun account was created in.">
          {(props) => (
            <Select value={region} onValueChange={(v) => setRegion(v as 'us' | 'eu')}>
              <SelectTrigger {...props} disabled={isLoading}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="us">US</SelectItem>
                <SelectItem value="eu">EU</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        <SecretField
          label="Private API key"
          hint="Mailgun dashboard → API Security."
          preview={data?.mailgunApiKey ?? { configured: false, last4: null, source: null }}
          value={apiKey}
          onChange={setApiKey}
          cleared={clearApiKey}
          onClear={() => {
            setClearApiKey((v) => !v)
            setApiKey('')
          }}
          error={fieldErrors?.mailgunApiKey?.[0]}
        />

        <SecretField
          label="Webhook signing key"
          hint="Mailgun dashboard → Sending → Webhooks, for this domain."
          preview={
            data?.mailgunWebhookSigningKey ?? { configured: false, last4: null, source: null }
          }
          value={webhookSigningKey}
          onChange={setWebhookSigningKey}
          cleared={clearWebhookSigningKey}
          onClear={() => {
            setClearWebhookSigningKey((v) => !v)
            setWebhookSigningKey('')
          }}
          error={fieldErrors?.mailgunWebhookSigningKey?.[0]}
        />

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * AI-assisted content editing (t0763) — two independent keys. Anthropic
 * powers the writing-help sparkle buttons on product/category descriptions;
 * OpenAI powers product-image generation. Either can be set without the other.
 */
function AiCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [anthropicApiKey, setAnthropicApiKey] = useState('')
  const [openaiApiKey, setOpenaiApiKey] = useState('')
  const [clearAnthropicApiKey, setClearAnthropicApiKey] = useState(false)
  const [clearOpenaiApiKey, setClearOpenaiApiKey] = useState(false)

  useEffect(() => {
    if (!data) return
    setAnthropicApiKey('')
    setOpenaiApiKey('')
    setClearAnthropicApiKey(false)
    setClearOpenaiApiKey(false)
  }, [data])

  const dirty = !!anthropicApiKey || !!openaiApiKey || clearAnthropicApiKey || clearOpenaiApiKey

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (clearAnthropicApiKey) patch.anthropicApiKey = null
    else if (anthropicApiKey) patch.anthropicApiKey = anthropicApiKey
    if (clearOpenaiApiKey) patch.openaiApiKey = null
    else if (openaiApiKey) patch.openaiApiKey = openaiApiKey
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>AI-assisted content editing</CardTitle>
        <CardDescription>
          Writing help and image generation for product and category content, available wherever a
          sparkle button appears. Usage is billed to whichever accounts these keys belong to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data ? (
          <Alert tone={data.aiTextEnabled || data.aiImageEnabled ? 'success' : 'info'}>
            {data.aiTextEnabled && data.aiImageEnabled
              ? 'Writing help and image generation are both on.'
              : data.aiTextEnabled
                ? 'Writing help is on. Image generation needs an OpenAI key.'
                : data.aiImageEnabled
                  ? 'Image generation is on. Writing help needs an Anthropic key.'
                  : 'Off — add a key below to turn on writing help or image generation.'}
          </Alert>
        ) : null}

        <SecretField
          label="Anthropic API key"
          hint="Powers writing help (draft, improve, shorten, fix grammar)."
          preview={data?.anthropicApiKey ?? { configured: false, last4: null, source: null }}
          value={anthropicApiKey}
          onChange={setAnthropicApiKey}
          cleared={clearAnthropicApiKey}
          onClear={() => {
            setClearAnthropicApiKey((v) => !v)
            setAnthropicApiKey('')
          }}
          error={fieldErrors?.anthropicApiKey?.[0]}
        />

        <SecretField
          label="OpenAI API key"
          hint="Powers product image generation and editing."
          preview={data?.openaiApiKey ?? { configured: false, last4: null, source: null }}
          value={openaiApiKey}
          onChange={setOpenaiApiKey}
          cleared={clearOpenaiApiKey}
          onClear={() => {
            setClearOpenaiApiKey((v) => !v)
            setOpenaiApiKey('')
          }}
          error={fieldErrors?.openaiApiKey?.[0]}
        />

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending || isLoading}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

function EmailCard({
  data,
  isLoading,
}: {
  data: IntegrationSettingsView | undefined
  isLoading: boolean
}) {
  const { save, fieldErrors } = useSaveIntegrationSettings()

  const [host, setHost] = useState('')
  const [port, setPort] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [fromAddress, setFromAddress] = useState('')
  const [clearPassword, setClearPassword] = useState(false)

  useEffect(() => {
    if (!data) return
    setHost(data.smtpHost.value ?? '')
    setPort(data.smtpPort.value ? String(data.smtpPort.value) : '')
    setUsername(data.smtpUsername.value ?? '')
    setPassword('')
    setFromAddress(data.smtpFromAddress.value ?? '')
    setClearPassword(false)
  }, [data])

  const hostChanged = data ? host !== (data.smtpHost.value ?? '') : false
  const portChanged = data
    ? port !== (data.smtpPort.value ? String(data.smtpPort.value) : '')
    : false
  const usernameChanged = data ? username !== (data.smtpUsername.value ?? '') : false
  const fromChanged = data ? fromAddress !== (data.smtpFromAddress.value ?? '') : false
  const dirty =
    hostChanged || portChanged || usernameChanged || fromChanged || !!password || clearPassword

  const onSave = () => {
    const patch: UpdateIntegrationSettingsInput = {}
    if (hostChanged) patch.smtpHost = host || null
    if (portChanged) patch.smtpPort = port ? Number(port) : null
    if (usernameChanged) patch.smtpUsername = username || null
    if (fromChanged) patch.smtpFromAddress = fromAddress || null
    if (clearPassword) patch.smtpPassword = null
    else if (password) patch.smtpPassword = password
    save.mutate(patch)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Email (SMTP)</CardTitle>
        <CardDescription>
          Fallback for sign-in codes and staff invites when Mailgun above is not configured. Mailgun
          takes over automatically once it is.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.mailgunApiKey.configured ? (
          <Alert tone="info">Mailgun is configured and is sending this mail instead.</Alert>
        ) : null}

        <Field label="Host">
          {(props) => (
            <Input
              {...props}
              value={host}
              disabled={isLoading}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.example.com"
            />
          )}
        </Field>

        <Field label="Port">
          {(props) => (
            <Input
              {...props}
              type="number"
              min={1}
              max={65535}
              value={port}
              disabled={isLoading}
              onChange={(e) => setPort(e.target.value)}
              invalid={!!fieldErrors?.smtpPort}
            />
          )}
        </Field>

        <Field label="Username">
          {(props) => (
            <Input
              {...props}
              value={username}
              disabled={isLoading}
              onChange={(e) => setUsername(e.target.value)}
            />
          )}
        </Field>

        <SecretField
          label="Password"
          preview={data?.smtpPassword ?? { configured: false, last4: null, source: null }}
          value={password}
          onChange={setPassword}
          cleared={clearPassword}
          onClear={() => {
            setClearPassword((v) => !v)
            setPassword('')
          }}
          error={fieldErrors?.smtpPassword?.[0]}
        />

        <Field label="From address">
          {(props) => (
            <Input
              {...props}
              type="email"
              value={fromAddress}
              disabled={isLoading}
              onChange={(e) => setFromAddress(e.target.value)}
              invalid={!!fieldErrors?.smtpFromAddress}
            />
          )}
        </Field>

        <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}
